import { EMPTY_STRING_INDICATOR } from "src/app/constants";
import { BUSINESS_UNIT_OPTIONS } from "src/forms/Cargo/options";

import type { SearchConfig } from "../types";

/**
 * The reviewed source of `cargo-search-config.json` in each stage's configs
 * bucket, and still read directly by the legacy Cargo component for its search
 * fields. Change it and the stored documents together. See
 * `SearchConfigRegistryEntry.searchConfig`.
 */
export const CARGO_SEARCH_CONFIG: SearchConfig = {
  lob: "cargo",
  claimType: "cargo",
  retrievalPageSize: 100,
  testId: "cargo-claims-search-results",

  searchableFields: [
    {
      fieldId: "vridLoadId",
      queryName: "byVridLoadId",
      queryInputProperty: "vridLoadId",
      maxTermLength: 100,
      labels: {
        buttonText: "Search",
        buttonTextSmall: "Search",
        placeholder: "Search for cargo claims using the VRID/Load ID",
        placeholderSmall: "Search by VRID/Load ID",
      },
      metrics: [
        {
          publisherName: "cargoSearchByVrid",
          counterName: { kind: "literal", value: "cargoSearchByVrid" },
        },
      ],
    },
  ],

  table: {
    columns: [
      {
        columnId: "vridLoadId",
        header: "VRID",
        source: "vridLoadId",
        sorting: { kind: "field", sortingField: "vridLoadId" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "claimNumber",
        header: "WKW Reference",
        source: "claimNumber",
        sorting: { kind: "field", sortingField: "claimNumber" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "businessUnit",
        header: "Business unit",
        source: "businessUnit",
        sorting: { kind: "field", sortingField: "businessUnit" },
        derivation: {
          kind: "optionLabel",
          options: BUSINESS_UNIT_OPTIONS,
          unmatchedText: "Not found",
        },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "claimSubmitterName",
        header: "Claim submitter name",
        source: "claimReporterFirstName",
        sorting: { kind: "field", sortingField: "claimReporterFirstName" },
        // Both parts are required because the cell this replaces renders the
        // placeholder unless first and last name are both present.
        derivation: {
          kind: "concatenate",
          separator: " ",
          parts: [
            { source: "claimReporterFirstName", required: true },
            { source: "claimReporterLastName", required: true },
          ],
        },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "purchaseOrderNumber",
        header: "PO#",
        source: "purchaseOrderNumber",
        sorting: { kind: "field", sortingField: "purchaseOrderNumber" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "createdAt",
        header: "Date claim reported",
        source: "createdAtTimestamp",
        sorting: { kind: "field", sortingField: "createdAtTimestamp" },
        derivation: { kind: "epochSecondsToDate" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "incidentDate",
        header: "Date of loss",
        source: "incidentDate",
        sorting: { kind: "field", sortingField: "incidentDate" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "totalClaimCurrencyCode",
        header: "Claim currency",
        source: "totalClaimCurrencyCode",
        sorting: { kind: "field", sortingField: "totalClaimCurrencyCode" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
    ],

    // The column id, unlike the `createdAt` sorting field it replaces, which was
    // not a returned claim attribute and so sorted nothing.
    defaultSorting: { columnId: "createdAt", direction: "descending" },

    pageSize: 10,
    minItemsForControls: 5,
    loadingText: "Loading Cargo Claims",
    resourceName: "Cargo Claim",
    resultsHeaderText: "Cargo Claims Search Results",
    filterPlaceholder: "Filter cargo claims",
    filterAriaLabel: "Filter cargo claims",
    // New for Cargo: `ListClaimsOutput.nextPageToken` makes "more claims
    // available" reachable here for the first time. The old adapter had to
    // report `false` unconditionally, since `ListCargoClaimsOutput` had no
    // such member.
    moreResultsSuffix: " (More claims available)",
  },

  exportConfig: {
    fileNamePrefix: "cargo_claims_search_results",
    fields: [
      { label: "VRID", source: "vridLoadId" },
      { label: "WKW Reference", source: "claimNumber" },
      {
        label: "Business unit",
        source: "businessUnit",
        derivation: {
          kind: "optionLabel",
          options: BUSINESS_UNIT_OPTIONS,
          unmatchedText: "Not found",
        },
      },
      {
        label: "Claim submitter name",
        source: "claimReporterFirstName",
        derivation: {
          kind: "concatenate",
          separator: " ",
          parts: [
            { source: "claimReporterFirstName", required: true },
            { source: "claimReporterLastName", required: true },
          ],
        },
      },
      { label: "PO#", source: "purchaseOrderNumber" },
      {
        label: "Date claim reported",
        source: "createdAtTimestamp",
        derivation: { kind: "epochSecondsToDate" },
      },
      { label: "Date of loss", source: "incidentDate" },
      { label: "Claim currency", source: "totalClaimCurrencyCode" },
    ],
  },

  metrics: {
    configRetrievalPublisherName: "claimsHistory.resolveSearchConfig",
    pageVisited: [
      {
        publisherName: "cargoSearchResultsVisited",
        counterName: { kind: "literal", value: "searchResultsVisited" },
      },
    ],
    searchSubmitted: [
      {
        publisherName: "cargoSearch",
        counterName: { kind: "literal", value: "cargoSearchBarUsed" },
      },
    ],
  },
};
